// Who is signed in, and who else works here — TODO T30 (1 Sep 2026); the calls are T17's and T18's.
//
// Two reads that answer the same question at two scales: `/me` is this session's recruiter (and
// the gate the whole app hangs off — a 403 here is `not_a_recruiter`, the state every newly
// invited recruiter starts in), and `/staff` is the colleagues a mandate can be assigned to.
//
// NEITHER IS A WRITE. `staff_operators` is system-owned: the BFF holds SELECT on it and nothing
// more, so a brain login is not a command channel and cannot enrol anyone
// (services/bff/README.md). The one write here is T176a's note, which is the signed-in recruiter's
// own record (console-owned `staff_notes`), never their staff row.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { AppearanceResponse, MeResponse, StaffNoteResponse, StaffRow } from '@vera/bff';
import { API_PREFIX, type RequestFn } from './core.ts';
import { POLL_MS, STALE_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

export const meApi = (request: RequestFn) => ({
  me: () => request<MeResponse>(`${API_PREFIX}/me`),
  listStaff: async () => (await request<{ staff: StaffRow[] }>(`${API_PREFIX}/staff`)).staff,
  note: () => request<StaffNoteResponse>(`${API_PREFIX}/me/note`),
  setNote: (note: string) =>
    request<StaffNoteResponse>(`${API_PREFIX}/me/note`, { method: 'PUT', body: { note } }),
  appearance: () => request<AppearanceResponse>(`${API_PREFIX}/me/appearance`),
  setAppearance: (change: Partial<Record<'theme' | 'accent', string>>) =>
    request<AppearanceResponse>(`${API_PREFIX}/me/appearance`, { method: 'PUT', body: change }),
});

/**
 * T570a (1 Nov 2026): the theme and accent saved to their account. Polled every minute, not on
 * focus (the app turns focus refetches off, `queryClient.ts`), so a change asked of the brain's
 * agent on WhatsApp shows in an open tab within one (Natanel, 31 Oct: "Save it to your account").
 */
export const APPEARANCE_POLL_MS = POLL_MS.slow;

export function useMyAppearance(): UseQueryResult<AppearanceResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['appearance'],
    queryFn: () => meApi(request).appearance(),
    refetchInterval: APPEARANCE_POLL_MS,
  });
}

/** Either or both; what is left out keeps what it was. */
export const useSetMyAppearance = () =>
  useConsoleMutation(
    (request, change: Partial<Record<'theme' | 'accent', string>>) =>
      meApi(request).setAppearance(change),
    KEYS.appearance,
  );

/**
 * T176a (30 Sep 2026): the recruiter's own note, which kadmon's brain reads every time they ask.
 * Polled slowly: it changes when they edit it here or say yes to a line she offers.
 */
export function useMyNote(): UseQueryResult<StaffNoteResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['note'],
    queryFn: () => meApi(request).note(),
    refetchInterval: POLL_MS.slow,
  });
}

/** Keep the note as written; empty clears it. */
export const useSetMyNote = () =>
  useConsoleMutation((request, note: string) => meApi(request).setNote(note), KEYS.note);

export function useMe(): UseQueryResult<MeResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => meApi(request).me(),
    // The identity behind the session does not change while a tab is open; the gate that matters
    // runs on every request at the service anyway.
    staleTime: STALE_MS.me,
  });
}

export function useStaff(): UseQueryResult<StaffRow[]> {
  const request = useRequest();
  // The staff list changes when somebody joins or leaves, which is not a polling cadence. It is
  // refetched after any write that could depend on it.
  return useQuery({
    queryKey: ['staff'],
    queryFn: () => meApi(request).listStaff(),
    staleTime: STALE_MS.staff,
  });
}
